import React, { useState, useEffect, useRef, useCallback } from "react";
import { StatsSelector, TeamSelector } from "../../components";
import { Container } from "./styled";

const GameContextSelector = ({
  appData,
  typeOfSelector,
  onChange,
  initialFilters,
}) => {
  const [state, setState] = useState({
    activeLeague: null,
    activePhase: null,
    activeSeason: null,
    startDate: null,
    endDate: null,
    activeRival: null,
    activeTeam: null,
    baseTeam: null,
    isH2HActive: null,
    games: null,
    statInX: null,
    statInY: null,
  });

  const teamSelectRef = useRef(null);
  const rivalSelectRef = useRef(null);

  const handleStateChange = useCallback(() => {
    if (onChange) {
      onChange(state);
    }
  }, [onChange, state]);

  useEffect(() => {
    handleStateChange();
  }, [handleStateChange]);

  const fetchGames = useCallback(
    async (season, league, team, startDate, endDate) => {
      const controller = new AbortController();

      try {
        let response = null;

        if (typeOfSelector !== "lineups") {
          if (season !== null) {
            response = await fetch(
              `https://deportactico-vercel-serverless.vercel.app/api/gamesByleague?season=${season}&league=${league}`
            );
          } else if (startDate !== null && endDate !== null) {
            response = await fetch(
              `https://deportactico-vercel-serverless.vercel.app/api/gamesByleague?league=${league}&startDate=${startDate}&endDate=${endDate}`
            );
          }
        } else if (typeOfSelector === "lineups" && team !== null) {
          const teamName = appData?.teams?.find((t) => t._id === team)?.name;

          if (season !== null) {
            response = await fetch(
              `https://deportactico-vercel-serverless.vercel.app/api/play-by-play?season=${season}&league=${league}&teamName=${encodeURIComponent(
                teamName
              )}&teamId=${team}`
            );
          } else if (startDate !== null && endDate !== null) {
            response = await fetch(
              `https://deportactico-vercel-serverless.vercel.app/api/play-by-play?league=${league}&startDate=${startDate}&endDate=${endDate}&teamName=${encodeURIComponent(
                teamName
              )}&teamId=${team}`
            );
          }
        } else return;

        if (!response) {
          setState((prev) => ({ ...prev, games: null }));
          onChange({ ...state, games: null });
          return;
        }

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (teamSelectRef.current) teamSelectRef.current.disabled = false;
        if (rivalSelectRef.current) rivalSelectRef.current.disabled = false;
        return result;
      } catch (error) {
        if (error.name !== "AbortError") console.error(error);
      }

      return () => controller.abort();
    },
    [appData, typeOfSelector, onChange]
  );

  useEffect(async () => {
    if (initialFilters) {
      const { activeSeason, activeLeague, activeTeam, startDate, endDate } =
        initialFilters;
      if (
        (activeLeague && activeSeason) ||
        (activeLeague && startDate && endDate)
      ) {
        let games = await fetchGames(
          activeSeason,
          activeLeague,
          activeTeam,
          startDate,
          endDate
        );
        await setState((prev) => ({
          ...prev,
          ...initialFilters,
          games: games,
        }));
      }
    }
  }, [initialFilters, fetchGames]);

  useEffect(async () => {
    if (
      (state.activeLeague && state.activeSeason) ||
      (state.activeLeague && state.startDate && state.endDate)
    ) {
      let games = await fetchGames(
        state.activeSeason,
        state.activeLeague,
        state.activeTeam,
        state.startDate,
        state.endDate
      );
      await setState((prev) => ({
        ...prev,
        ...initialFilters,
        games: games,
      }));
    } else {
      setState((prev) => ({ ...prev, games: null }));
    }
  }, [
    state.activeSeason,
    state.activePhase,
    state.activeLeague,
    state.activeTeam,
    state.startDate,
    state.endDate,
    fetchGames,
  ]);

  const handleStatChange = useCallback((contextData, isStatInX) => {
    if (isStatInX) {
      setState((prev) => ({ ...prev, statInX: contextData }));
    } else setState((prev) => ({ ...prev, statInY: contextData }));
  }, []);

  const handleTeamChange = useCallback(
    (contextData, isActiveTeam, isBaseTeam) => {
      if (isActiveTeam) {
        setState((prev) => ({ ...prev, activeTeam: contextData }));
      } else setState((prev) => ({ ...prev, activeRival: contextData }));
      if (isBaseTeam !== null)
        setState((prev) => ({ ...prev, baseTeam: isBaseTeam }));
    },
    []
  );

  return (
    <Container>
      <div className={`headder-container `}>
        <div className="headder-options-container">
          <div className="option-content">
            <label>Liga</label>
            <select
              id="league-select"
              value={state.activeLeague}
              onChange={(e) => {
                setState((prev) => ({
                  ...prev,
                  activeLeague: e.target.value,
                }));
              }}
            >
              <option hidden defaultValue>
                Selecciona liga
              </option>
              <option>LNB</option>
              <option>LA</option>
            </select>
          </div>

          <div className="option-content">
            <label>Fase</label>
            <select
              id="phase-select"
              value={state.activePhase}
              onChange={(e) => {
                e.target.value !== "Temporada completa"
                  ? setState((prev) => ({
                      ...prev,
                      activePhase: e.target.value,
                    }))
                  : setState((prev) => ({
                      ...prev,
                      activePhase: null,
                    }));
              }}
            >
              <option hidden defaultValue>
                Selecciona Fase
              </option>
              <option>Temporada completa</option>
              <option>Fase regular</option>
              <option>Play in</option>
              <option>Reclasificación</option>
              <option>Playoff</option>
              <option>Permanencia</option>
            </select>
          </div>

          <div className="option-content">
            <label>Temporada</label>
            <select
              id="season-select"
              value={state.activeSeason}
              onChange={(e) => {
                if (e.target.value === "Periodo personalizado") {
                  let popUp = document.getElementById("date-pop-up");
                  popUp.classList.add("active");
                  setState((prev) => ({
                    ...prev,
                    activeSeason: null,
                  }));
                } else
                  setState((prev) => ({
                    ...prev,
                    activeSeason: e.target.value,
                    startDate: null,
                    endDate: null,
                  }));
              }}
            >
              <option hidden>Selecciona temporada</option>
              <option>2023/2024</option>
              <option>2024/2025</option>
              <option>2025/2026</option>
            </select>
          </div>
          {typeOfSelector === "statistics" ? (
            <StatsSelector onChange={handleStatChange} isStatInX={true} />
          ) : typeOfSelector === "lineups" ? (
            <TeamSelector
              onChange={handleTeamChange}
              appData={appData}
              activeLeague={state.activeLeague}
              activePhase={state.activePhase}
              activeSeason={state.activeSeason}
              typeOfSelector={typeOfSelector}
              isActiveTeam={true}
              startDate={state.startDate}
              endDate={state.endDate}
            />
          ) : null}
        </div>
        {typeOfSelector === "analytics" ? (
          <div className="headder-teams-container">
            <TeamSelector
              onChange={handleTeamChange}
              appData={appData}
              activeLeague={state.activeLeague}
              activePhase={state.activePhase}
              activeSeason={state.activeSeason}
              typeOfSelector={typeOfSelector}
              isActiveTeam={true}
            />
            <div className="flex_center_space-between">
              <input
                onChange={(e) => {
                  setState((prev) => ({
                    ...prev,
                    isH2HActive: e.target.checked,
                  }));
                }}
                type="checkbox"
              />
              <h3>Head to Head</h3>
            </div>
            <TeamSelector
              onChange={handleTeamChange}
              appData={appData}
              activeLeague={state.activeLeague}
              activePhase={state.activePhase}
              activeSeason={state.activeSeason}
              typeOfSelector={typeOfSelector}
              isActiveTeam={false}
            />
          </div>
        ) : typeOfSelector === "scatterPlots" ? (
          <div className="flex_center_space-between">
            <StatsSelector onChange={handleStatChange} isStatInX={true} />
            <StatsSelector onChange={handleStatChange} isStatInX={false} />
          </div>
        ) : null}

        {state.isLoading && (
          <div className="loading-indicator">
            <p>Cargando juegos...</p>
          </div>
        )}
      </div>
    </Container>
  );
};

export default GameContextSelector;
